import { Injectable } from '@angular/core';
import {
  MetadataChangeActionInput,
  MetadataChangeCreateInput,
  MetadataChangeEditInput,
  MetadataChangeRequest,
  GetMetadataChangesForSurveyQueryVariables,
  GetMetadataChangesForSurveyQuery,
  AcceptMetadataChangeMutationVariables,
  AcceptMetadataChangeMutation,
  DeclineMetadataChangeMutationVariables,
  DeclineMetadataChangeMutation,
  EditMetadataChangeMutationVariables,
  EditMetadataChangeMutation,
  CreateMetadataChangeMutationVariables,
  CreateMetadataChangeMutation,
} from '../../../awsAppSync/API';
import { getMetadataChangesForSurvey } from '../../../awsAppSync/graphql/queries';
import {
  acceptMetadataChange,
  declineMetadataChange,
  editMetadataChange,
  createMetadataChange,
} from '../../../awsAppSync/graphql/mutations';
import { BaseAWSRequestService } from '../../shared/service/base-services/base-aws-request.service';

// Type-only re-exports for consumers of this service's request/response shapes.
// `export type … from` keeps isolatedModules happy (TS1205).
export type {
  MetadataChangeRequest,
  MetadataChangeConnection,
} from '../../../awsAppSync/API';

@Injectable({
  providedIn: 'root',
})
export class MetadataChangeService {
  constructor(private awsService: BaseAWSRequestService) {}

  public async getMetadataChangesForSurvey(
    primaryCompanyId: string,
    surveyId: string,
    status?: string,
  ): Promise<MetadataChangeRequest[] | null> {
    const variables: GetMetadataChangesForSurveyQueryVariables = {
      primaryCompanyId,
      surveyId,
      ...(status && { status }),
    };
    try {
      const result = await this.awsService.executeGetQuery<
        GetMetadataChangesForSurveyQuery
      >(getMetadataChangesForSurvey, variables);
      const items = result?.getMetadataChangesForSurvey?.items;
      return items
        ? items.filter((item): item is MetadataChangeRequest => item !== null)
        : null;
    } catch (error) {
      console.error('Error fetching metadata changes for survey:', error);
      return null;
    }
  }

  public async acceptMetadataChange(
    input: MetadataChangeActionInput,
  ): Promise<MetadataChangeRequest | null> {
    try {
      const variables: AcceptMetadataChangeMutationVariables = { input };
      const result =
        await this.awsService.executeMutation<AcceptMetadataChangeMutation>(
          acceptMetadataChange,
          variables,
        );
      return result?.acceptMetadataChange ?? null;
    } catch (error: any) {
      if (this.isConflictError(error)) {
        throw error;
      }
      console.error('Error accepting metadata change:', error);
      return null;
    }
  }

  public async declineMetadataChange(
    input: MetadataChangeActionInput,
  ): Promise<MetadataChangeRequest | null> {
    try {
      const variables: DeclineMetadataChangeMutationVariables = { input };
      const result =
        await this.awsService.executeMutation<DeclineMetadataChangeMutation>(
          declineMetadataChange,
          variables,
        );
      return result?.declineMetadataChange ?? null;
    } catch (error: any) {
      if (this.isConflictError(error)) {
        throw error;
      }
      console.error('Error declining metadata change:', error);
      return null;
    }
  }

  public async editMetadataChange(
    input: MetadataChangeEditInput,
  ): Promise<MetadataChangeRequest | null> {
    try {
      const variables: EditMetadataChangeMutationVariables = { input };
      const result =
        await this.awsService.executeMutation<EditMetadataChangeMutation>(
          editMetadataChange,
          variables,
        );
      return result?.editMetadataChange ?? null;
    } catch (error: any) {
      if (this.isConflictError(error)) {
        throw error;
      }
      console.error('Error editing metadata change:', error);
      // Re-thrown so the grid can surface the reason, e.g. a duplicate asset name (SYRC-7292).
      throw error;
    }
  }

  /**
   * Creates a new metadata change (reviewer-initiated edit) when no existing
   * change record exists for the asset/field. The backend creates a record in
   * EDITED status (or updates an existing PENDING/EDITED record), so the edit
   * persists and is applied on publish.
   *
   * Errors are re-thrown so callers can surface a clear failure message rather
   * than silently discarding the edit (SYRC-7292 AC 4).
   */
  public async createMetadataChange(
    input: MetadataChangeCreateInput,
  ): Promise<MetadataChangeRequest | null> {
    try {
      const variables: CreateMetadataChangeMutationVariables = { input };
      const result =
        await this.awsService.executeMutation<CreateMetadataChangeMutation>(
          createMetadataChange,
          variables,
        );
      return result?.createMetadataChange ?? null;
    } catch (error: any) {
      if (this.isConflictError(error)) {
        throw error;
      }
      console.error('Error creating metadata change:', error);
      throw error;
    }
  }

  /**
   * Checks if an error is a concurrent resolution conflict
   * (ConditionalCheckFailedException or "already resolved" from the backend).
   */
  private isConflictError(error: any): boolean {
    const message = error?.message ?? error?.errors?.[0]?.message ?? '';
    return (
      message.includes('ConditionalCheckFailedException') ||
      message.includes('already resolved')
    );
  }
}

